import Layout from "../components/Layout";

/**
 * Drivons Privacy Policy — reflects the ACTUAL data flows in the app.
 * Reviewed for Google Play Data Safety declaration + Apple Nutrition Label.
 * Update this file whenever a new third-party service is integrated.
 */
export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 prose prose-invert" data-testid="privacy-page">
        <h1>Drivons — Privacy Policy</h1>
        <p><i>Last updated: February 2026</i></p>

        <h2>1. Who we are</h2>
        <p>Drivons is an on-demand marketplace connecting riders and customers
        with drivers, movers, tow trucks, mobile mechanics, tire technicians,
        delivery couriers, luxury chauffeurs, and mobile car-wash services.
        This policy explains what data we collect, why, who we share it with,
        and how you can access or delete your data.</p>

        <h2>2. Data we collect</h2>
        <ul>
          <li><b>Account:</b> name, email, phone, hashed password, profile role
          (customer / supplier / admin), preferred language.</li>
          <li><b>Location:</b> pickup + drop-off coordinates for the orders you
          create. Live location while an order is in progress (drivers only).</li>
          <li><b>Payment:</b> we do NOT store card numbers. PayPal handles all
          payments; we keep only the PayPal transaction id, amount, currency,
          and payment status. For Turkish customers whose local currency is
          not directly supported by PayPal, we convert the amount to USD using
          European Central Bank rates via frankfurter.dev (fallback: open.er-api.com)
          and store both the original TRY amount and the USD amount on the
          transaction record for reconciliation.</li>
          <li><b>Order history:</b> pickup / drop-off addresses, category,
          fare breakdown, driver/customer id, timestamps, ratings.</li>
          <li><b>Chat:</b> messages between riders and drivers during an order.</li>
          <li><b>Push notification tokens:</b> Firebase Cloud Messaging device
          tokens so we can notify you about ride status, split-payment
          requests, and weekly earnings (drivers).</li>
        </ul>

        <h2>3. Third parties we share data with</h2>
        <ul>
          <li><b>PayPal (Live):</b> receives your name, email, order total,
          currency to process the payment. See PayPal's privacy policy.</li>
          <li><b>OSRM / Nominatim:</b> anonymous coordinate pairs are sent to
          the OSRM demo server to compute road distance + reverse-geocode
          addresses. No account identifiers are attached.</li>
          <li><b>Firebase Cloud Messaging:</b> device tokens + notification
          payloads (no PII beyond your device id).</li>
          <li><b>Frankfurter.dev / open.er-api.com:</b> only the currency pair
          (e.g. TRY→USD) is sent — no user data.</li>
        </ul>
        <p>We do NOT sell your data. We do NOT use ad-tracking SDKs.</p>

        <h2>4. Your rights</h2>
        <ul>
          <li><b>Access & export:</b> download all your data as JSON from
          <code>Account → Download my data</code>.</li>
          <li><b>Deletion:</b> permanently delete your account from
          <code>Account → Delete my account</code>. Order history is retained
          anonymously for financial audit only.</li>
          <li><b>Rectification:</b> update your profile from the same page.</li>
          <li><b>Portability:</b> the JSON export uses a standard schema.</li>
        </ul>

        <h2>5. Data retention</h2>
        <ul>
          <li>Active accounts: as long as the account exists.</li>
          <li>Deleted accounts: profile fields are erased immediately;
          order + payment records are kept anonymized for 7 years to
          comply with financial-audit regulations in Israel and Turkey.</li>
          <li>Push tokens: cleared immediately on logout.</li>
        </ul>

        <h2>6. Security</h2>
        <p>All traffic is TLS 1.2+. Passwords are hashed with bcrypt (cost 12).
        Admin access requires JWT + role check. PayPal webhooks are
        signature-verified against our production webhook id before any DB
        write.</p>

        <h2>7. Children</h2>
        <p>Drivons is not intended for children under 16.</p>

        <h2>8. Contact</h2>
        <p>Questions? Email <a className="text-brand-accent underline" href="mailto:privacy@drivon.com">privacy@drivon.com</a>.</p>
      </div>
    </Layout>
  );
}
